import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium focus-warm " +
    "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground neu-pressable glow-warm-sm glow-warm-hover hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground transition-colors hover:bg-secondary/80 hover:text-foreground",
        outline:
          "border border-input bg-background transition-colors hover:bg-accentWarm/10 hover:border-accentWarm/40 hover:text-foreground",
        destructive:
          "bg-destructive text-destructive-foreground neu-pressable hover:bg-destructive/90 " +
          "shadow-[3px_3px_8px_hsl(var(--destructive)/0.35),-1px_-1px_4px_hsl(var(--neu-shadow-light)/0.7)]",
        ghost:
          "transition-colors hover:bg-accentWarm/10 hover:text-foreground",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-10 rounded-md px-8",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
